import React from 'react'
import {colors} from '../../../sharedComponents/colors'
import styled, {css} from 'styled-components'
import {useTranslation} from 'react-i18next'
import {roundToTwoDecimal} from '../../../pages/tasks/CompletedTasks'
import {SmartAsset} from '../../../Components/Atoms/ImageLoader'
import {AlarmSubType} from '../../../state/alarms/state'
import {MeasurementMethodOption, TaskAlarmDetails} from '../../../state/home/site/state'
import {hasAlarmType} from '../../../pages/tasks/CompletedTasks'
import {
  capitalizeFirstLetter,
  getHumanizedDateString,
  getHumanizedDateWithTime,
  parseStringToDate
} from '../../../Components/Atoms/Utils'
import {TFunction} from 'i18next'
import {Subtask} from 'state/performTask/state'
import {DataField, IconPalette, SharedIcon, Text} from '../../../sharedComponents/components'
import {useAppState} from '../../../state'
import {normalizeSites} from '../../../config/utils'

const commonStyles = css`
  display: grid;
  margin: 1rem 0;
  grid-template-columns: 4% 55% auto 15%;
  grid-template-rows: auto auto;
  row-gap: 1rem;
`
const deleteGridRow = css`
  '. deleteEvent . .';
`
const GridDataField = ({gridArea, title, content}: any) => {
  return (
    <div style={gridArea}>
      <DataField valueSize="S" labelSize="XS" label={title} value={content} />
    </div>
  )
}

const WasteReasonArea = ({reason}: any) => {
  const {t} = useTranslation('tasks')
  return (
    <GridDataField
      gridArea={{gridArea: 'reason'}}
      title={t('tasks:labels.reason', 'Reason for waste')}
      content={reason || '-'}
    />
  )
}

const CompletedByArea = ({completedBy}: any) => {
  const {t} = useTranslation('tasks')
  return (
    <GridDataField
      gridArea={{gridArea: 'completedBy'}}
      title={t('tasks:labels.completedByArea', 'Completed by')}
      content={completedBy || '-'}
    />
  )
}

const NoteArea = ({notes}: any) => {
  const {t} = useTranslation('tasks')
  return <GridDataField gridArea={{gridArea: 'note'}} title={t('tasks:labels.note', 'Note')} content={notes || '-'} />
}

const SubtasksArea = ({tasks}: any) => {
  const {t} = useTranslation('tasks')
  const subtasks =
    Object.prototype.toString.call(tasks) === '[object Array]'
      ? tasks.map((task: any) => (typeof task === 'string' ? task : task.name)).join(' / ')
      : tasks
  return (
    <GridDataField
      gridArea={{gridArea: 'subtasks'}}
      title={t('tasks:labels.subtasks', 'Subtasks')}
      content={subtasks || '-'}
    />
  )
}

const SubtasksAreaWithActors = ({tasks}: any) => {
  const {t} = useTranslation('tasks')
  const subtasks: Subtask[] = tasks
    .map((task: Subtask) => `${task.subtaskName} (${task.completedBy || 'N/A'})`)
    .join(' / \n')
  return (
    <GridDataField
      gridArea={{gridArea: 'subtasks'}}
      title={t('tasks:labels.subtasks', 'Subtasks')}
      content={subtasks || '-'}
    />
  )
}

const RecipientsArea = ({recipients}: any) => {
  const {t} = useTranslation('tasks')
  const content = !recipients || !Array.isArray(recipients) || recipients.length === 0 ? '-' : recipients.join(', ')
  return (
    <GridDataField
      gridArea={{gridArea: 'recipients'}}
      title={t('tasks:labels.recipient', 'Recipients')}
      content={content}
    />
  )
}

const SupplierArea = ({suppliers}: any) => {
  const {t} = useTranslation('tasks')
  const content = !suppliers || !Array.isArray(suppliers) || suppliers.length === 0 ? '-' : suppliers.join(', ')
  return (
    <GridDataField
      gridArea={{gridArea: 'suppliers'}}
      title={t('tasks:labels.supplier', 'Supplier')}
      content={content}
    />
  )
}

const SubtaskDetailsTable = ({item}: any) => {
  const {t} = useTranslation('tasks')
  const options = Array.isArray(item.selectedSubtaskOptions) ? item.selectedSubtaskOptions : []
  const comments = Array.isArray(item.subtaskComments) ? item.subtaskComments : []
  const relatedSites = Array.isArray(item.relatedSites) ? item.relatedSites : []
  const subtaskNames = Array.from(
    new Set([
      ...(Array.isArray(item.doneSubtasks) ? item.doneSubtasks : []),
      ...options.map((entry: any) => entry.subtaskName),
      ...comments.map((entry: any) => entry.subtaskName)
    ])
  )

  if (!subtaskNames.length && !options.length && !comments.length) return null

  return (
    <SubtaskDetailsWrapper>
      <SubtaskDetailsTitle>{t('tasks:labels.subtaskDetails', 'Subtask details')}</SubtaskDetailsTitle>
      <SubtaskDetailsTableElement>
        <thead>
          <tr>
            <th>{t('tasks:labels.subtask', 'Subtask')}</th>
            <th>{t('tasks:labels.option', 'Option')}</th>
            <th>{t('tasks:labels.comments', 'Comments')}</th>
            <th>{t('tasks:labels.relatedSite', 'Related site')}</th>
          </tr>
        </thead>
        <tbody>
          {subtaskNames.map((subtaskName: string) => {
            const option = options.find((entry: any) => entry.subtaskName === subtaskName)?.option
            const comment = comments.find((entry: any) => entry.subtaskName === subtaskName)?.comment
            const completedBy = item.subTasksWithActors?.find((entry: any) => entry.subtaskName === subtaskName)
              ?.completedBy
            return (
              <tr key={subtaskName}>
                <td>{`${subtaskName || '-'} (${completedBy || 'N/A'})`}</td>
                <td>{option || '-'}</td>
                <td>{comment || '-'}</td>
                <td>{relatedSites.length ? relatedSites.join(', ') : '-'}</td>
              </tr>
            )
          })}
        </tbody>
      </SubtaskDetailsTableElement>
    </SubtaskDetailsWrapper>
  )
}

const CompletedTimeArea = ({
  completedTime,
  recordingDay,
  completedTimestamp,
  locale,
  showRecordingDay = false
}: any) => {
  const {t} = useTranslation('tasks')
  const content = (
    <div>
      <div>
        <Text size="S" color={colors.brand.cobalt}>
          {completedTime || '-'}
        </Text>
      </div>
      {showRecordingDay &&
      recordingDay &&
      completedTimestamp &&
      new Date(recordingDay).toISOString() !== new Date(completedTimestamp).toISOString() ? (
        <div>
          <Text size="XS" color={colors.system.grey_50}>
            {t('tasks:labels.recordingDay', 'Recording day')}
          </Text>
          <br />
          <Text size="S" color={colors.brand.cobalt}>
            {getHumanizedDateString(new Date(recordingDay), locale)}
          </Text>
        </div>
      ) : null}
    </div>
  )

  return (
    <GridDataField
      gridArea={{gridArea: 'completedTime'}}
      title={t('tasks:labels.completedTime', 'Completed time')}
      content={content}
    />
  )
}

const CompletedTimeDurationArea = ({
  startTime,
  endTime,
  duration,
  alarms,
  recordingDay,
  completedTimestamp,
  locale,
  showRecordingDay = false
}: any) => {
  const {t} = useTranslation('tasks')
  const coolingAlarms = Array.isArray(alarms) ? (alarms as TaskAlarmDetails[]) : undefined
  const hasCoolingTimeExtended = coolingAlarms ? hasAlarmType(coolingAlarms, AlarmSubType.CoolingTime) : false
  const content =
    startTime && endTime && typeof duration === 'number'
      ? `${startTime} -> ${endTime} (${duration} ${t('tasks:labels.minutes', 'minutes')})`
      : '-'
  const durationContent = (
    <Text size="S" color={hasCoolingTimeExtended ? colors.system.red : colors.brand.cobalt}>
      {content}
    </Text>
  )
  const fullContent = (
    <div>
      {durationContent}
      {showRecordingDay &&
      recordingDay &&
      completedTimestamp &&
      new Date(recordingDay).toISOString() !== new Date(completedTimestamp).toISOString() ? (
        <div>
          <Text size="XS" color={colors.system.grey_50}>
            {t('tasks:labels.recordingDay', 'Recording day')}
          </Text>
          <br />
          <Text size="S" color={hasCoolingTimeExtended ? colors.system.red : colors.brand.cobalt}>
            {getHumanizedDateString(new Date(recordingDay), locale)}
          </Text>
        </div>
      ) : null}
    </div>
  )

  return (
    <GridDataField
      gridArea={{gridArea: 'completedTime'}}
      title={`${t('tasks:labels.completedTime', 'Completed time')} (${t('tasks:labels.duration', 'Duration')})`}
      content={fullContent}
    />
  )
}

const getMeasurementMethodLabel = (method: any, t: TFunction) => {
  const getTranslatedLabel = (label?: string) => {
    switch (label) {
      case MeasurementMethodOption.MANUAL:
        return t('tasks:labels.manualMeasuring', 'manual')
      case MeasurementMethodOption.DEVICE:
        return t('tasks:labels.deviceMeasuring', 'device')
      default:
        return '-'
    }
  }
  const getCapitalizedLabel = (method: MeasurementMethodOption) => {
    const label = getTranslatedLabel(method)
    return capitalizeFirstLetter(label)
  }

  const formatMethodLabel = (methods: string[]) => {
    const first = getTranslatedLabel(methods[0])
    const second = getTranslatedLabel(methods[1])
    return first === second
      ? capitalizeFirstLetter(first)
      : capitalizeFirstLetter(`${first} ${t('common:labels.and', 'and')} ${second}`)
  }

  if (Array.isArray(method)) {
    return method.length === 2 ? formatMethodLabel(method) : getCapitalizedLabel(method[0])
  }
  return '-'
}

const MeasurementTargetArea = ({target}: any) => {
  const {t} = useTranslation('tasks')
  return (
    <GridDataField
      gridArea={{gridArea: 'measurementTarget'}}
      title={t('tasks:labels.measurementValueTarget', 'Measurement target')}
      content={target || '-'}
    />
  )
}

const isPreparationTask = (taskGroupName?: string) => (taskGroupName || '').trim().toLowerCase() === 'preparation'

const AromiDishArea = ({aromiProduct, taskGroupName}: any) => {
  const {t} = useTranslation('tasks')
  const {state} = useAppState()
  const selectedChain = state.selectedChainId ? (state.chainsById as any)?.[state.selectedChainId] : undefined
  const currentSite =
    state.site?.id && selectedChain
      ? normalizeSites(selectedChain).find((candidate: any) => candidate.id === state.site?.id) || state.site
      : state.site

  if (!currentSite?.aromiIntegration || !isPreparationTask(taskGroupName)) {
    return null
  }

  return (
    <GridDataField
      gridArea={{gridArea: 'aromiDish'}}
      title={t('tasks:labels.aromiDish', 'Aromi dish')}
      content={aromiProduct || '-'}
    />
  )
}

const MeasurementMethodArea = ({method}: any) => {
  const {t} = useTranslation(['tasks', 'common'])
  const label = getMeasurementMethodLabel(method, t)
  return (
    <GridDataField
      gridArea={{gridArea: 'measurementMethod'}}
      title={t('tasks:labels.measurementMethod', 'Measurement method')}
      content={label}
    />
  )
}

const SampleCollectionDateArea = ({samplecollectionDate, locale}: any) => {
  const {t} = useTranslation('tasks')
  const content = samplecollectionDate ? getHumanizedDateString(new Date(samplecollectionDate), locale) : '-'
  return (
    <GridDataField
      gridArea={{gridArea: 'sampleCollectionDate'}}
      title={t('tasks:labels.sampleCollectionDate', 'Sample collection date')}
      content={content}
    />
  )
}

const ResultTargetAreaForCooling = ({min, max, unit}: any) => {
  const {t} = useTranslation('tasks')
  const roundedMin = typeof min === 'number' ? roundToTwoDecimal(min) : undefined
  const roundedMax = typeof max === 'number' ? roundToTwoDecimal(max) : undefined
  const content =
    roundedMin !== undefined && roundedMax !== undefined
      ? `Min ${roundedMin}°${unit} -> ${roundedMax}°${unit} Max`
      : '-'
  return <GridDataField gridArea={{gridArea: 'limit'}} title={t('tasks:labels.limit', 'Limit')} content={content} />
}

const PeakTemperatureArea = ({peak, alarms, unit}: any) => {
  const {t} = useTranslation('tasks')
  const coolingAlarms = Array.isArray(alarms) ? (alarms as TaskAlarmDetails[]) : undefined
  const hasLowLimitAlarm = coolingAlarms ? hasAlarmType(coolingAlarms, AlarmSubType.Low) : false
  const formatedPeak = typeof peak === 'number' ? String(roundToTwoDecimal(peak)) : ''
  const content = `${formatedPeak}°${unit}`
  const peakContent = (
    <Text size="S" color={hasLowLimitAlarm ? colors.system.red : colors.brand.cobalt}>
      {content}
    </Text>
  )
  return (
    <GridDataField
      gridArea={{gridArea: 'peakTemperature'}}
      title={t('tasks:labels.peakTemperature', 'Peak temperature')}
      content={peakContent}
    />
  )
}

const ResultTargetAreaForMinMax = ({min, max, unit, isTemperature}: any) => {
  const {t} = useTranslation('tasks')
  const roundedMin = typeof min === 'number' ? roundToTwoDecimal(min) : undefined
  const roundedMax = typeof max === 'number' ? roundToTwoDecimal(max) : undefined
  const displayUnit = !!isTemperature ? `°${unit}` : ` ${unit}`
  const content =
    roundedMin !== undefined && roundedMax !== undefined
      ? `Min ${roundedMin}${displayUnit} / ${roundedMax}${displayUnit} Max`
      : '-'
  return <GridDataField gridArea={{gridArea: 'limit'}} title={t('tasks:labels.limit', 'Limit')} content={content} />
}

const ResultTargetArea = ({max, unit}: any) => {
  const {t} = useTranslation('tasks')
  const measurementUnit = unit ? unit : t('tasks:labels.unitPlaceholder', 'unit')
  const roundedMax = typeof max === 'number' ? roundToTwoDecimal(max) : undefined
  const content = roundedMax !== undefined ? `Max ${roundedMax} ${measurementUnit}` : '-'
  return <GridDataField gridArea={{gridArea: 'limit'}} title={t('tasks:labels.limit', 'Limit')} content={content} />
}

const AssetThumb = styled.div`
  width: 140px;
  height: 125px;
  flex: 0 0 auto;
  position: relative;
  border: 1px solid ${colors.system.grey_5};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`

const AssetThumbnail = ({asset}: {asset: any}) => {
  const {actions} = useAppState()

  const openAssetInNewTab = async () => {
    try {
      const d = await actions.getDownloadUrl(asset.id)
      if (d && (d as any).downloadURL) {
        window.open((d as any).downloadURL, '_blank', 'noopener')
      }
    } catch (err) {
      console.error('Error opening asset', err)
    }
  }

  return (
    <AssetThumb title={asset?.name || asset?.id} onClick={openAssetInNewTab}>
      <SmartAsset asset={asset as any} />
    </AssetThumb>
  )
}

const ImageBlock = ({image, images}: any) => {
  const {t} = useTranslation('tasks')
  const allAssets = React.useMemo(() => {
    const list = Array.isArray(images) ? [...images] : []
    if (image && image.id && !list.some((a: any) => a?.id === image.id)) {
      list.unshift(image)
    }
    return list
  }, [images, image])

  const hasAny = Array.isArray(allAssets) && allAssets.length > 0
  return (
    <div style={{gridArea: 'image'}}>
      <div>
        <Text size="XS" color={colors.system.grey_50}>
          {t('tasks:labels.imageOrVideo', 'Image / video')}
        </Text>
      </div>
      <div style={{margin: '0.25rem 0 0 0'}}>
        {hasAny ? (
          <div style={{display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start'}}>
            {allAssets.slice(0, 3).map((a: any) => (
              <AssetThumbnail key={a.id} asset={a} />
            ))}
          </div>
        ) : (
          <Text size="S">-</Text>
        )}
      </div>
    </div>
  )
}

const ScheduledTimeArea = ({scheduledAt, locale}: any) => {
  const {t} = useTranslation('tasks')
  const content = scheduledAt ? getHumanizedDateWithTime(parseStringToDate(scheduledAt), locale) : '-'
  return (
    <GridDataField
      gridArea={{gridArea: 'scheduledTime'}}
      title={t('tasks:labels.scheduledTime', 'Scheduled Time')}
      content={content}
    />
  )
}

const DeleteEvent = ({deleteHandler}: any) => {
  const {t} = useTranslation('tasks')
  return (
    <DeleteAction onClick={deleteHandler} style={{gridArea: 'deleteEvent'}}>
      <SharedIcon icon={IconPalette.Waste} width="16px" height="16px" fill={colors.brand.brightRed} />
      <Text size="S" color={colors.brand.brightRed} style={{paddingLeft: 6}}>
        {t('tasks:labels.deleteTask', 'Delete task')}
      </Text>
    </DeleteAction>
  )
}

const DeleteAction = styled.a`
  cursor: pointer;
`

const AutomaticCoolingExpandedRowContainer = styled.div`
  ${commonStyles}
  grid-template-areas:
    '. measurementTarget completedTime limit'
    '. aromiDish completedBy peakTemperature'
    '. note . .'
    '. scheduledTime . .'
    '. image . .';
`

export const AutomaticCoolingExpandedRow = ({item, unit, recordingDay, completedTimestamp, locale}: any) => {
  return (
    <AutomaticCoolingExpandedRowContainer>
      <MeasurementTargetArea target={item.measurementTarget} />
      <AromiDishArea aromiProduct={item.aromiProduct} taskGroupName={item.taskGroupName} />
      <NoteArea notes={item.notes} />
      <CompletedByArea completedBy={item.reportedActor} />
      <CompletedTimeDurationArea
        startTime={item.coolingStartedTime}
        endTime={item.coolingEndedTime}
        duration={item.coolingDuration}
        alarms={item.alarms}
        recordingDay={recordingDay}
        completedTimestamp={completedTimestamp}
        locale={locale}
      />
      <ResultTargetAreaForCooling min={item.maxValue} max={item.minValue} unit={unit} />
      <PeakTemperatureArea peak={item.cycleLog?.firstMeasurement} alarms={item.alarms} unit={unit} />
      <ScheduledTimeArea scheduledAt={item.scheduledAt} locale={locale} />
      <ImageBlock image={item.asset} images={item.eventAssets} />
    </AutomaticCoolingExpandedRowContainer>
  )
}

const CoolingExpandedRowContainer = styled.div`
  ${commonStyles}
  grid-template-areas:
    '. measurementTarget completedTime limit'
    '. aromiDish completedBy measurementMethod'
    '. note . .'
    '. scheduledTime . .'
    '. image . .'
  ${deleteGridRow};
`

export const CoolingExpandedRow = ({item, unit, showDelete, recordingDay, completedTimestamp, locale}: any) => {
  return (
    <CoolingExpandedRowContainer>
      <MeasurementTargetArea target={item.measurementTarget} />
      <AromiDishArea aromiProduct={item.aromiProduct} taskGroupName={item.taskGroupName} />
      <NoteArea notes={item.notes} />
      <CompletedByArea completedBy={item.reportedActor} />
      <MeasurementMethodArea method={item.measurementMethod} />
      <CompletedTimeDurationArea
        startTime={item.coolingStartedTime}
        endTime={item.coolingEndedTime}
        duration={item.coolingDuration}
        alarms={item.alarms}
        recordingDay={recordingDay}
        completedTimestamp={completedTimestamp}
        locale={locale}
      />
      <ScheduledTimeArea scheduledAt={item.scheduledAt} locale={locale} />
      <ImageBlock image={item.asset} images={item.eventAssets} />
      <ResultTargetAreaForCooling min={item.maxValue} max={item.minValue} unit={unit} />
    </CoolingExpandedRowContainer>
  )
}

const ManualExpandedRowContainer = styled.div`
  ${commonStyles}
  grid-template-areas:
    '. measurementTarget completedTime limit'
    '. aromiDish completedBy .'
    '. note . .'
    '. scheduledTime . .'
    '. image . .'
    ${deleteGridRow};
`

export const ManualExpandedRow = ({item, showDelete, recordingDay, completedTimestamp, locale}: any) => {
  return (
    <ManualExpandedRowContainer>
      <MeasurementTargetArea target={item.measurementTarget} />
      <AromiDishArea aromiProduct={item.aromiProduct} taskGroupName={item.taskGroupName} />
      <NoteArea notes={item.notes} />
      <CompletedByArea completedBy={item.reportedActor} />
      <CompletedTimeArea
        completedTime={item.taskCompletedTime}
        recordingDay={recordingDay}
        completedTimestamp={completedTimestamp}
        locale={locale}
      />
      <ScheduledTimeArea scheduledAt={item.scheduledAt} locale={locale} />
      <ImageBlock image={item.asset} images={item.eventAssets} />
      <ResultTargetAreaForMinMax min={item.minValue} max={item.maxValue} unit={item.unit} isTemperature={false} />
    </ManualExpandedRowContainer>
  )
}

const TodoExpandedRowContainer = styled.div`
  ${commonStyles}
  grid-template-areas:
    '. completedTime . .'
    '. note completedBy .'
    '. scheduledTime . .'
    '. image . .'
    ${deleteGridRow};
`

const SubtaskDetailsWrapper = styled.div`
  grid-column: 2 / -2;
  margin-top: 0.5rem;
  overflow-x: auto;
`

const SubtaskDetailsTitle = styled.h4`
  margin: 0 0 0.5rem;
  color: ${colors.brand.cobalt};
  font-size: 0.875rem;
`

const SubtaskDetailsTableElement = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: ${colors.brand.cobalt};
  font-size: 0.875rem;

  th,
  td {
    padding: 0.5rem 0.75rem;
    border: 1px solid ${colors.system.grey_5};
    text-align: left;
    vertical-align: top;
  }

  th {
    color: ${colors.system.grey_50};
    font-weight: 600;
  }
`

export const TodoExpandedRow = ({item, showDelete, recordingDay, completedTimestamp, locale}: any) => {
  return (
    <TodoExpandedRowContainer>
      <NoteArea notes={item.notes} />
      <CompletedByArea completedBy={item.reportedActor} />
      <CompletedTimeArea
        completedTime={item.taskCompletedTime}
        recordingDay={recordingDay}
        completedTimestamp={completedTimestamp}
        locale={locale}
      />
      <ScheduledTimeArea scheduledAt={item.scheduledAt} locale={locale} />
      <ImageBlock image={item.asset} images={item.eventAssets} />
      <SubtaskDetailsTable item={item} />
    </TodoExpandedRowContainer>
  )
}

const SingleChoiceExpandedRowContainer = styled.div`
  ${commonStyles}
  grid-template-areas:
    '. subtasks completedTime .'
    '. note completedBy .'
    '. scheduledTime . .'
    '. image . .'
    ${deleteGridRow};
`

export const SingleChoiceExpandedRow = ({item, showDelete, recordingDay, completedTimestamp, locale}: any) => {
  return (
    <SingleChoiceExpandedRowContainer>
      {item.subTasksWithActors && item.subTasksWithActors.length > 0 ? (
        <SubtasksAreaWithActors tasks={item.subTasksWithActors} />
      ) : (
        <SubtasksArea tasks={item.choice || item.doneSubtasks} />
      )}
      <NoteArea notes={item.notes} />
      <CompletedByArea completedBy={item.reportedActor} />
      <CompletedTimeArea
        completedTime={item.taskCompletedTime}
        recordingDay={recordingDay}
        completedTimestamp={completedTimestamp}
        locale={locale}
      />
      <ScheduledTimeArea scheduledAt={item.scheduledAt} locale={locale} />
      <ImageBlock image={item.asset} images={item.eventAssets} />
      <SubtaskDetailsTable item={item} />
    </SingleChoiceExpandedRowContainer>
  )
}

const WasteWithReasonRowContainer = styled.div`
  ${commonStyles}
  grid-template-areas:
    '. measurementTarget completedTime limit'
    '. aromiDish completedBy reason'
    '. note . .'
    '. scheduledTime . .'
    '. image . .'
    ${deleteGridRow};
`

export const WasteWithReasonExpandedRow = ({item, unit, showDelete, recordingDay, completedTimestamp, locale}: any) => {
  return (
    <WasteWithReasonRowContainer>
      <MeasurementTargetArea target={item.measurementTarget} />
      <AromiDishArea aromiProduct={item.aromiProduct} taskGroupName={item.taskGroupName} />
      <NoteArea notes={item.notes} />
      <CompletedByArea completedBy={item.reportedActor} />
      <CompletedTimeArea
        completedTime={item.taskCompletedTime}
        recordingDay={recordingDay}
        completedTimestamp={completedTimestamp}
        locale={locale}
        showRecordingDay
      />
      <WasteReasonArea reason={item.reasonForWaste} />
      <ScheduledTimeArea scheduledAt={item.scheduledAt} locale={locale} />
      <ImageBlock image={item.asset} images={item.eventAssets} />
      <ResultTargetArea max={item.maxValue} unit={item.unit || unit} />
    </WasteWithReasonRowContainer>
  )
}

const WasteExpandedRowContainer = styled.div`
  ${commonStyles}
  grid-template-areas:
    '. measurementTarget completedTime limit'
    '. aromiDish completedBy .'
    '. note . .'
    '. scheduledTime . .'
    '. image . .'
    ${deleteGridRow};
`

export const WasteExpandedRow = ({item, unit, showDelete, recordingDay, completedTimestamp, locale}: any) => {
  return (
    <WasteExpandedRowContainer>
      <MeasurementTargetArea target={item.measurementTarget} />
      <AromiDishArea aromiProduct={item.aromiProduct} taskGroupName={item.taskGroupName} />
      <NoteArea notes={item.notes} />
      <CompletedByArea completedBy={item.reportedActor} />
      <CompletedTimeArea
        completedTime={item.taskCompletedTime}
        recordingDay={recordingDay}
        completedTimestamp={completedTimestamp}
        locale={locale}
        showRecordingDay
      />
      <ScheduledTimeArea scheduledAt={item.scheduledAt} locale={locale} />
      <ImageBlock image={item.asset} images={item.eventAssets} />
      <ResultTargetArea max={item.maxValue} unit={item.unit || unit} />
    </WasteExpandedRowContainer>
  )
}

const HygieneExpandedRowContainer = styled.div`
  ${commonStyles}
  grid-template-areas:
    '. measurementTarget completedTime limit'
    '. aromiDish sampleCollectionDate measurementMethod'
    '. note completedBy .'
    '. scheduledTime . .'
    '. image . .'
    ${deleteGridRow};
`

export const HygieneExpandedRow = ({item, unit, showDelete, recordingDay, completedTimestamp, locale}: any) => {
  return (
    <HygieneExpandedRowContainer>
      <MeasurementTargetArea target={item.measurementTarget} />
      <AromiDishArea aromiProduct={item.aromiProduct} taskGroupName={item.taskGroupName} />
      <NoteArea notes={item.notes} />
      <CompletedByArea completedBy={item.reportedActor} />
      <MeasurementMethodArea method={item.measurementMethod} />
      <CompletedTimeArea
        completedTime={item.taskCompletedTime}
        recordingDay={recordingDay}
        completedTimestamp={completedTimestamp}
        locale={locale}
      />
      <SampleCollectionDateArea samplecollectionDate={item.samplecollectionDate} locale={locale} />
      <ScheduledTimeArea scheduledAt={item.scheduledAt} locale={locale} />
      <ImageBlock image={item.asset} images={item.eventAssets} />
      <ResultTargetArea max={item.maxValue} unit={item.unit || unit} />
    </HygieneExpandedRowContainer>
  )
}

const TemperatureExpandedRowContainer = styled.div<{$isFoodDelivery: boolean; $isReceiving: boolean}>`
  ${commonStyles}
  grid-template-areas:
    '. measurementTarget completedTime limit'
    '. aromiDish completedBy measurementMethod'
    '. note . .'
    ${({$isFoodDelivery, $isReceiving}) =>
      $isFoodDelivery ? "'. recipients . .'" : $isReceiving ? "'. suppliers . .'" : ''}
    '. scheduledTime . .'
    '. image . .'
    ${deleteGridRow};
`

export const TemperatureExpandedRow = ({item, unit, showDelete, recordingDay, completedTimestamp, locale}: any) => {
  const {state} = useAppState()
  const isFoodDelivery = item.taskGroupName === 'Food delivery'
  const isReceiving = item.taskGroupName === 'Receiving'
  const supplierNormalized = normalizeRecipients(item.reportedSupplier)
  return (
    <TemperatureExpandedRowContainer $isFoodDelivery={isFoodDelivery} $isReceiving={isReceiving}>
      <MeasurementTargetArea target={item.measurementTarget} />
      <AromiDishArea aromiProduct={item.aromiProduct} taskGroupName={item.taskGroupName} />
      <NoteArea notes={item.notes} />
      {isFoodDelivery && <RecipientsArea recipients={normalizeRecipients(item.reportedRecipient)} />}
      {isReceiving && <SupplierArea suppliers={supplierNormalized} />}
      <CompletedByArea completedBy={item.reportedActor} />
      <MeasurementMethodArea method={item.measurementMethod} />
      <CompletedTimeArea completedTime={item.taskCompletedTime} recordingDay={recordingDay} locale={locale} />
      <ScheduledTimeArea scheduledAt={item.scheduledAt} locale={locale} />
      <ImageBlock image={item.asset} images={item.eventAssets} />
      <ResultTargetAreaForMinMax min={item.minValue} max={item.maxValue} unit={unit} isTemperature={true} />
    </TemperatureExpandedRowContainer>
  )
}

export const ErrorExpandedRow = () => (
  <Text size="S" color={colors.system.red}>
    {'Error: this tasktype layout is not defined'}
  </Text>
)

const normalizeRecipients = (value: any) => {
  if (!value) return undefined
  if (Array.isArray(value)) return value
  if (typeof value === 'string') {
    const s = value.trim()
    try {
      const parsed = JSON.parse(s)
      if (Array.isArray(parsed)) return parsed
      if (typeof parsed === 'string') return [parsed]
      if (parsed && typeof parsed === 'object') {
        const vals = Object.values(parsed).filter(v => typeof v === 'string')
        if (vals.length) return vals
      }
    } catch (e) {
      // fallback heuristics
      let cleaned = s
      if (cleaned.startsWith('"') && cleaned.endsWith('"')) cleaned = cleaned.slice(1, -1)
      if ((cleaned.startsWith('{') && cleaned.endsWith('}')) || (cleaned.startsWith('[') && cleaned.endsWith(']'))) {
        cleaned = cleaned.slice(1, -1)
      }
      cleaned = cleaned.replace(/^"|"$/g, '')
      const parts = cleaned
        .split(',')
        .map(p => p.trim())
        .filter(p => p.length > 0)
      if (parts.length) return parts
      return [cleaned]
    }
  }
  return undefined
}
